import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/hooks/useMeta";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import { Loading, ErrorState } from "@/components/common/States";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate, fmtCurrency } from "@/lib/format";
import { ArrowLeft, Phone, Mail, MapPin, Globe, GitBranch, RefreshCcw, Plus, Lock, Pencil, Trash2, Activity as ActIcon } from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import LeadEditDialog from "@/components/leads/LeadEditDialog";
import LeadStatusControl from "@/components/leads/LeadStatusControl";

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="grid place-items-center h-9 w-9 rounded-lg bg-slate-100 text-slate-500 shrink-0"><Icon className="h-4 w-4" /></div>
    <div className="min-w-0"><div className="text-xs text-slate-400">{label}</div><div className="text-sm font-medium text-slate-800 truncate">{value || "—"}</div></div>
  </div>
);

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const meta = useMeta();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [act, setAct] = useState({ type: "Phone Call", outcome: "", notes: "", next_action: "", next_action_date: "" });
  const [reassign, setReassign] = useState({ to_user_id: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const isManager = ["manager", "super_admin"].includes(user?.role);
  const canReassign = isManager;
  const isAdmin = user?.role === "super_admin";
  const canChangeStatus = ["super_admin", "manager", "field_executive", "telecaller"].includes(user?.role);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const doDelete = async () => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      navigate("/leads");
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      const a = await api.get("/activities", { params: { lead_id: id } });
      setActivities(a.data.items);
    } catch (e) {
      setError(apiError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (reassignOpen) api.get("/users/managers").catch(() => ({ data: [] })); api.get("/users").then(({ data }) => setManagers(data)).catch(() => {}); }, [reassignOpen]);

  const logActivity = async () => {
    if (!act.type) return;
    setBusy(true);
    try {
      await api.post("/activities", { ...act, lead_id: id });
      toast.success("Activity logged");
      setLogOpen(false);
      setAct({ type: "Phone Call", outcome: "", notes: "", next_action: "", next_action_date: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/deals", { title: `${lead.org_name} — Deal`, lead_id: id, stage: "Qualified", amount: lead.estimated_value || null });
      toast.success("Converted to a deal");
      navigate("/pipeline");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doReassign = async () => {
    if (!reassign.to_user_id || !reassign.reason.trim()) return toast.error("Select a user and enter a reason");
    setBusy(true);
    try {
      await api.post(`/leads/${id}/reassign`, reassign);
      toast.success("Lead reassigned");
      setReassignOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!lead) return <Loading label="Loading lead…" />;

  const introMsg = `Hello ${lead.contact_person || "there"}, this is ${user?.full_name} from Medoho regarding ${lead.org_name}.`;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/leads")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal" data-testid="lead-back-btn"><ArrowLeft className="h-4 w-4" /> All leads</button>

      <PageHeader
        title={lead.org_name}
        subtitle={`${lead.lead_type} lead${lead.duplicate_status && lead.duplicate_status !== "none" ? " · duplicate-checked" : ""}`}
        testid="lead-detail-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {canChangeStatus ? <LeadStatusControl lead={lead} onChanged={() => load()} /> : <StatusBadge status={lead.status} testid="lead-status-badge" />}
            <WhatsAppButton phone={lead.whatsapp || lead.mobile} message={introMsg} leadId={id} templateKey="first_introduction" />
            <Button variant="outline" onClick={() => setLogOpen(true)} className="h-11" data-testid="log-activity-btn"><Plus className="h-4 w-4" /> Log Activity</Button>
            <Button onClick={convert} disabled={busy} className="bg-teal hover:bg-teal-700 h-11" data-testid="convert-deal-btn"><GitBranch className="h-4 w-4" /> Convert to Deal</Button>
            {canReassign && <Button variant="outline" onClick={() => setReassignOpen(true)} className="h-11" data-testid="reassign-btn"><RefreshCcw className="h-4 w-4" /> Reassign</Button>}
            {isAdmin && <Button variant="outline" onClick={() => setEditOpen(true)} className="h-11" data-testid="lead-detail-edit-btn"><Pencil className="h-4 w-4" /> Edit</Button>}
            {isAdmin && <Button variant="outline" onClick={() => setDeleteOpen(true)} className="h-11 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="lead-detail-delete-btn"><Trash2 className="h-4 w-4" /> Delete</Button>}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Contact & organization</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <Info icon={Phone} label="Mobile" value={lead.mobile} />
              <Info icon={Phone} label="WhatsApp" value={lead.whatsapp} />
              <Info icon={Mail} label="Email" value={lead.email} />
              <Info icon={MapPin} label="City" value={lead.city} />
              <Info icon={MapPin} label="Address" value={lead.address} />
              <Info icon={Globe} label="Website" value={lead.website} />
            </div>
            <div className="grid sm:grid-cols-3 gap-5 mt-5 pt-5 border-t border-slate-100">
              <div><div className="text-xs text-slate-400">Estimated value</div><div className="text-sm font-semibold text-slate-800">{fmtCurrency(lead.estimated_value)}</div></div>
              <div><div className="text-xs text-slate-400">Source</div><div className="text-sm font-semibold text-slate-800">{lead.source || "—"}</div></div>
              <div><div className="text-xs text-slate-400">Owner</div><div className="text-sm font-semibold text-slate-800">{lead.owner_name || "Partner lead"}</div></div>
            </div>
            {lead.requirement && <div className="mt-5 pt-5 border-t border-slate-100"><div className="text-xs text-slate-400 mb-1">Requirement</div><p className="text-sm text-slate-700">{lead.requirement}</p></div>}
            {lead.next_action && <div className="mt-4 rounded-lg bg-teal/5 border border-teal/20 p-3 text-sm"><b className="text-teal">Next action:</b> {lead.next_action}{lead.next_action_date && <span className="text-slate-500"> · due {fmtDate(lead.next_action_date)}</span>}</div>}
            {lead.private_notes && <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2"><Lock className="h-4 w-4 mt-0.5 shrink-0" /><div><b>Private notes:</b> {lead.private_notes}</div></div>}
          </div>

          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><ActIcon className="h-5 w-5 text-teal" /> Activity timeline</h3>
            {activities.length === 0 ? (
              <EmptyState title="No activities yet" description="Log a call, WhatsApp, visit or note to build the timeline." icon={ActIcon} />
            ) : (
              <div className="space-y-4">
                {activities.map((a) => (
                  <div key={a.id} className="flex gap-3" data-testid={`activity-${a.id}`}>
                    <div className="flex flex-col items-center"><div className="h-2.5 w-2.5 rounded-full bg-teal mt-1.5" /><div className="w-px flex-1 bg-slate-200" /></div>
                    <div className="pb-2 min-w-0">
                      <div className="text-sm font-semibold text-slate-800">{a.type} <span className="font-normal text-slate-400">· {a.user_name}</span></div>
                      {a.outcome && <div className="text-xs text-slate-500">Outcome: {a.outcome}</div>}
                      {a.notes && <div className="text-sm text-slate-600 mt-0.5">{a.notes}</div>}
                      <div className="text-xs text-slate-400 mt-0.5">{fmtDate(a.when || a.created_at, true)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Record details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Created</span><span className="text-slate-700">{fmtDate(lead.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Last activity</span><span className="text-slate-700">{fmtDate(lead.last_activity_at)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Duplicate check</span><StatusBadge status={lead.duplicate_status || "none"} /></div>
              <div className="flex justify-between"><span className="text-slate-400">Protected until</span><span className="text-slate-700">{fmtDate(lead.ownership_protection_until)}</span></div>
            </div>
          </div>
        </div>
      </div>

      {isAdmin && <LeadEditDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} onSaved={() => load()} />}
      <ConfirmDialog open={deleteOpen} onOpenChange={setDeleteOpen} destructive title="Delete this lead?"
        confirmLabel="Delete lead" onConfirm={doDelete} testid="lead-delete-dialog"
        description={`"${lead.org_name}" will be removed from all lists (soft delete, kept in the audit trail).`} />

      {/* Log activity dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent data-testid="log-activity-dialog">
          <DialogHeader><DialogTitle>Log activity</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={act.type} onValueChange={(v) => setAct({ ...act, type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta.activity_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Outcome</Label><Input className="mt-1.5" value={act.outcome} onChange={(e) => setAct({ ...act, outcome: e.target.value })} data-testid="activity-outcome-input" /></div>
            <div><Label>Notes</Label><Textarea className="mt-1.5" rows={2} value={act.notes} onChange={(e) => setAct({ ...act, notes: e.target.value })} data-testid="activity-notes-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Next action</Label><Input className="mt-1.5" value={act.next_action} onChange={(e) => setAct({ ...act, next_action: e.target.value })} /></div>
              <div><Label>Next action date</Label><Input type="date" className="mt-1.5" value={act.next_action_date} onChange={(e) => setAct({ ...act, next_action_date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogOpen(false)}>Cancel</Button>
            <Button onClick={logActivity} disabled={busy} className="bg-teal hover:bg-teal-700" data-testid="save-activity-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader><DialogTitle>Reassign lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Assign to</Label>
              <Select value={reassign.to_user_id} onValueChange={(v) => setReassign({ ...reassign, to_user_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="reassign-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{managers.filter((m) => m.role !== "sales_partner").map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name} · {m.role_label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason (required)</Label><Textarea className="mt-1.5" rows={2} value={reassign.reason} onChange={(e) => setReassign({ ...reassign, reason: e.target.value })} data-testid="reassign-reason-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
            <Button onClick={doReassign} disabled={busy} className="bg-teal hover:bg-teal-700" data-testid="save-reassign-btn">Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
